import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
// import { useAppContext } from '../context/AppContext';
import { useTranslation } from "../context/TranslationContext";
import "../styles/ClientDetails.css";
import {
  uploadFileToSignedUrl,
  fetchClientMatterDetails,
  fetchSalesforceFiles,
  fetchDownloadUrl,
  downloadFileFromSignedUrl,
  getUploadSignedUrl,
  postFileUploadComplete,
  fetchFilesPreferGrouped,
  fetchClientPartyDetails,
} from "../helpers/apiHelper";
import { FaCog } from "react-icons/fa";
import { formatDateToDDMMYYYY, formatDateToYYYYMMDD } from "../helpers/utilityHelper";
import PartyAlertsBanner from "./PartyAlertsBanner";
import { CompensationTable, DetailChip, DetailField, DetailsBackLink } from "./DetailsLayout";
import { useAuth } from "../context/AuthContext";
import CategorizedDocuments, { buildMissingDocumentsAlert } from "./CategorizedDocuments";
import { useDocumentPreview } from "./DocumentViewer";
import { isMockDocumentId } from "../helpers/documentCategories";
import useCategorizedDocuments from "../hooks/useCategorizedDocuments";
import { useWhatsAppMatter } from "../context/WhatsAppContactContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import TaxIdChangeAction from "./TaxIdChangeAction";
import { isTaxIdChangeEnabled } from "../helpers/taxIdChangeHelper";
import "../styles/Documents.css";
import { FiCopy } from "react-icons/fi";
import "../styles/Views.css";
import "../styles/PartyDetails.css";

const valueOrDash = (value) => value || "—";

const ClientDetailsMatter = () => {
  const { t } = useTranslation();
  const { matterId } = useParams();
  const navigate = useNavigate();
  const [matter, setMatter] = useState(null);
  const [loadingMatter, setLoadingMatter] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fileInputKey] = useState(Date.now());
  const [statusMessage, setStatusMessage] = useState(null);
  const { tokens, user } = useAuth();
  const location = useLocation();
  const partyId = matter?.details?.partyId || location.state?.partyId;
  const matterName = matter?.details?.name || location.state?.matterName;
  useWhatsAppMatter(matter?.details?.name);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const listPreview = useDocumentPreview({
    fetchViewUrl: (doc) => fetchDownloadUrl(doc.id, tokens.idToken),
    onDownload: (doc) => handleDownload(doc.id, doc.name),
    onError: (key) => setDownloadError(t(key)),
  });
  const { features } = useFeatureFlags();
  const taxIdChangeEnabled = isTaxIdChangeEnabled(features);
  const [party, setParty] = useState(null);
  const [partyReloadKey, setPartyReloadKey] = useState(0);
  const loadFiles = useCallback(
    () => fetchFilesPreferGrouped(fetchSalesforceFiles, matterId, tokens.idToken),
    [matterId, tokens.idToken],
  );
  const docs = useCategorizedDocuments({ load: loadFiles, matterId, token: tokens.idToken, author: user?.email });
  const { reload: reloadDocuments } = docs;
  const documents = docs.files;
  const loadingDocs = docs.loading;
  const documentsError = docs.failed ? t("clientDetails.documentLoadError") : "";

  useEffect(() => {
    const token = tokens.idToken;

    const fetchMatter = async () => {
      if (!matterId || !token) {
        setLoadingMatter(false);
        return false;
      }

      try {
        const result = await fetchClientMatterDetails(token, matterId);
        setMatter(result);
        return true; // success
      } catch (err) {
        navigate("/404", { replace: true });
        return false; // failed
      } finally {
        setLoadingMatter(false);
      }
    };

    // Conditionally fetch documents only if matter fetch succeeds
    fetchMatter().then((success) => {
      if (success) {
        reloadDocuments();
      }
    });
  }, [matterId, tokens.idToken, navigate, reloadDocuments]);

  // The CPF/CNPJ shortcut needs the party's permissions; it loads quietly and never blocks the matter.
  useEffect(() => {
    if (!taxIdChangeEnabled || !partyId || !tokens.idToken) {
      setParty(null);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 35000);
    fetchClientPartyDetails(tokens.idToken, partyId, controller.signal, { redirectOnServerError: false })
      .then((result) => {
        if (!cancelled) setParty(result || null);
      })
      .catch(() => {
        if (!cancelled) setParty(null);
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
    // A token refresh must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxIdChangeEnabled, partyId, partyReloadKey]);

  const refreshAfterTaxIdChange = () => {
    setPartyReloadKey((key) => key + 1);
    reloadDocuments({ silent: true });
  };

  const refreshMatterAfterApply = () => {
    fetchClientMatterDetails(tokens.idToken, matterId)
      .then((result) => {
        if (result) setMatter(result);
      })
      .catch(() => {});
  };

  const goBack = () => (location.key === "default" ? navigate("/") : navigate(-1));
  const backLink = <DetailsBackLink label={t("back")} onClick={goBack} />;

  if (loadingMatter) {
    return (
      <div className="portal-page client-details-container party-details matter-details">
        {backLink}
        <header className="portal-page-panel party-header-card">
          <p className="portal-page-kicker">{t("breadcrumb.matterDetails")}</p>
          <p className="party-loading" role="status">
            <FaCog className="spinner-cog" aria-hidden="true" />
            {t("loading")}
          </p>
        </header>
      </div>
    );
  }

  // if (!matter) {
  //   return <div className="client-details-container">{t('clientsTable.clientNotFound')}</div>;
  // }

  const handleDownload = async (fileId, fileName) => {
    setDownloadError(null);
    if (isMockDocumentId(fileId)) {
      setDownloadError(t("documents.errors.notAvailable"));
      return;
    }
    try {
      setDownloadingFileId(fileId);
      const token = tokens.idToken;
      const signedUrl = await fetchDownloadUrl(fileId, token);
      await downloadFileFromSignedUrl(signedUrl, fileName);
    } catch (error) {
      console.error("Failed to download document:", error);
      setDownloadError(t("documents.errors.download"));
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleCopy = (field, link) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const refreshDocuments = () => reloadDocuments({ silent: true });

  const compensationRecords = matter?.compensation?.records || [];
  const hasCompensation = compensationRecords.length > 0;
  const hasDiscontinuance = matter?.discontinuance?.available;
  const termination = matter?.termination;
  const isTerminated = Boolean(termination);

  const details = matter?.details || {};
  const copyButton = (field, link, emptyLabel) => (link ? (
    <button
      type="button"
      className={`party-copy-button${copiedField === field ? " is-copied" : ""}`}
      onClick={() => handleCopy(field, link)}
      data-testid={`matter-copy-${field}`}
    >
      <FiCopy aria-hidden="true" />
      {copiedField === field ? t("clientDetails.matter.copied") : t("click_to_copy")}
    </button>
  ) : <span>{emptyLabel}</span>);

  return (
    <div className="portal-page client-details-container party-details matter-details">
      {backLink}

      <PartyAlertsBanner
        alerts={matter.alerts || []}
        extraItems={buildMissingDocumentsAlert(docs.missing, t, docs.focusCategory)}
      />

      <section className="portal-page-panel party-main-card" aria-labelledby="matter-name-title">
        <header className="party-main-header">
          <p className="portal-page-kicker">{t("breadcrumb.matterDetails")}</p>
          <h2 className="party-name" id="matter-name-title" data-testid="matter-party-name">
            {valueOrDash(details.partyName)}
          </h2>
          {(details.name || details.bhpMasterSchedule) && (
            <ul className="party-chips">
              {details.name && (
                <DetailChip label={t("clientsTable.matterName")} testId="matter-chip-matter">{details.name}</DetailChip>
              )}
              {details.bhpMasterSchedule && (
                <DetailChip label={t("clientsTable.masterSchedule")} testId="matter-chip-master-schedule">
                  {details.bhpMasterSchedule}
                </DetailChip>
              )}
            </ul>
          )}
        </header>

        <div className="party-columns">
          <section className="party-column" aria-labelledby="matter-client-title">
            <div className="party-card-header">
              <h3 id="matter-client-title">{t("clientDetails.matter.clientSection")}</h3>
            </div>
            <div className="party-field-grid is-two-columns">
              <DetailField label={t("clientsTable.partyName")}>
                {partyId ? (
                  <button
                    type="button"
                    className="party-chip-link"
                    data-testid="matter-party-link"
                    aria-label={t("clientDetails.matter.openParty").replace("{name}", details.partyName || "")}
                    onClick={() => navigate(
                      `/client-details-party/${encodeURIComponent(partyId)}?matterId=${encodeURIComponent(matterId)}`,
                      { state: { matterId, matterName } },
                    )}
                  >
                    {valueOrDash(details.partyName)}
                  </button>
                ) : (
                  <span>{valueOrDash(details.partyName)}</span>
                )}
              </DetailField>
              <DetailField
                label={t("clientsTable.cpf")}
                value={valueOrDash(details.cpf)}
                testId="matter-cpf"
                flag={taxIdChangeEnabled && party && (
                  <TaxIdChangeAction
                    party={party}
                    partyId={partyId}
                    features={features}
                    token={tokens.idToken}
                    author={user?.email}
                    t={t}
                    matterId={matterId}
                    matterName={matterName}
                    variant="shortcut"
                    onRefresh={refreshAfterTaxIdChange}
                    onApplied={refreshMatterAfterApply}
                  />
                )}
              />
              <DetailField
                label={t("clientsTable.email")}
                value={details.email || t("clientsTable.emptyStates.noEmailAddress")}
                testId="matter-email"
              />
              <DetailField
                label={t("clientsTable.phone")}
                value={details.phone || t("clientsTable.emptyStates.noPhoneNumber")}
                testId="matter-phone"
              />
            </div>
          </section>

          <section className="party-column" aria-labelledby="matter-contract-title">
            <div className="party-card-header">
              <h3 id="matter-contract-title">{t("clientDetails.matter.contractSection")}</h3>
            </div>
            <div className="party-field-grid is-two-columns">
              <DetailField
                label={t("clientsTable.retainerSignedDate")}
                value={formatDateToYYYYMMDD(details.retainedSignedDate) || t("clientsTable.emptyStates.notYetSigned")}
                testId="matter-retainer-signed"
              />
              <DetailField label={t("clientsTable.retainerSignUrl")}>
                {copyButton("retainerSignUrl", details.retainerSignUrl, t("clientsTable.emptyStates.noRetainerUrl"))}
              </DetailField>
              <DetailField
                label={t("clientsTable.questionnaireCompletedDate")}
                value={formatDateToYYYYMMDD(details.questionnaireCompletedAt)
                  || t("clientsTable.emptyStates.notYetCompleted")}
                testId="matter-questionnaire-completed"
              />
              <DetailField label={t("clientsTable.questionnaireLink")}>
                {copyButton(
                  "questionnaireLink",
                  details.questionnaireLink,
                  t("clientsTable.emptyStates.noQuestionnaireLink"),
                )}
              </DetailField>
            </div>
          </section>
        </div>
      </section>

      {hasCompensation && (
        <section className="portal-page-panel party-card" aria-labelledby="matter-compensation-title">
          <h3 id="matter-compensation-title">{t("clientDetails.compensation.title")}</h3>
          <CompensationTable records={compensationRecords} t={t} testId="matter-compensation-table" />
        </section>
      )}

      {hasDiscontinuance && (
        <section className="portal-page-panel party-card" aria-labelledby="matter-discontinuance-title">
          <h3 id="matter-discontinuance-title">{t("clientsTable.discontinuance")}</h3>
          <div className="party-field-grid">
            <DetailField
              label={t("clientsTable.discontinuanceStatus")}
              value={t(
                `clientsTable.discontinuanceStatuses.${(matter.discontinuance?.status || "unknown").toLowerCase()}`,
              )}
            />
            <DetailField
              label={t("clientsTable.questionnaireCompleted")}
              value={matter.discontinuance?.questionnaireCompleted ? t("coordinatorDetails.yes") : t("coordinatorDetails.no")}
            />
          </div>
        </section>
      )}

      {isTerminated && (
        <section className="portal-page-panel party-card termination-panel" aria-labelledby="matter-termination-title">
          <h3 id="matter-termination-title">{t("clientsTable.termination.title")}</h3>
          <div className="party-field-grid">
            <DetailField label={t("clientsTable.termination.reason")} value={valueOrDash(termination.reason)} />
            <DetailField
              label={t("clientsTable.termination.date")}
              value={formatDateToDDMMYYYY(termination.terminatedDate || termination.closedDate) || "—"}
            />
          </div>
        </section>
      )}

      <section className="portal-page-panel party-card party-documents-card" aria-labelledby="matter-documents-title">
      <h3 id="matter-documents-title">{t("clientDetails.documentsTitle") || "Documents"}</h3>
      {downloadError && (
        <div className="status-message is-error" role="alert" data-testid="docs-download-error">{downloadError}</div>
      )}

      {loadingDocs ? (
        <p className="mb-10 flex-row">
          <FaCog className="spinner-cog" />
          {t("loading") || "Loading documents..."}
        </p>
      ) : docs.grouped ? (
        <CategorizedDocuments
          data={docs.grouped}
          canEdit={docs.grouped.canEdit}
          uploadsDisabled={isTerminated}
          onView={(doc) => handleDownload(doc.id, doc.name)}
          viewingId={downloadingFileId}
          fetchViewUrl={(doc) => fetchDownloadUrl(doc.id, tokens.idToken)}
          onUpload={docs.upload}
          onReclassify={docs.reclassify}
          focusRequest={docs.focusRequest}
          idPrefix="matter-docs"
        />
      ) : documents.length > 0 ? (
        <div className="client-documents">
          {documents.map((doc, index) => (
            <div className="flex-row" key={index}>
              <button
                className="link"
                onClick={() => {
                  setDownloadError(null);
                  listPreview.open(doc);
                }}
                disabled={downloadingFileId === doc.id || listPreview.previewingId === doc.id}
              >
                {doc.name}
              </button>
              {(downloadingFileId === doc.id || listPreview.previewingId === doc.id) && <FaCog className="spinner-cog ml-2" />}
            </div>
          ))}
        </div>
      ) : (
        <p className="mb-10 text-color-primary ">{t("clientDetails.noDocumentsFound") || "No documents found."}</p>
      )}
      {listPreview.viewerElement}

      {docs.grouped ? null : isTerminated ? (
        <p className="mb-10 text-color-primary">{t("clientsTable.termination.uploadsDisabled")}</p>
      ) : (
      <div className="upload-section">
        <label htmlFor="file-upload" className={`custom-file-upload ${uploading ? "uploading" : ""}`} style={{ cursor: uploading ? "default" : "pointer" }}>
          {uploading ? t("clientDetails.uploading") : t("clientDetails.uploadNewDoc")}
        </label>
        <input
          id="file-upload"
          key={fileInputKey}
          type="file"
          accept=".jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,application/pdf"
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            setUploading(true);
            setStatusMessage(null);

            try {
              const token = tokens.idToken;
              const fileData = {
                name: file.name,
                description: "",
                author: user?.email,
                folderPath: [],
                tags: [],
                relatedObjectId: matterId,
                relatedObjectName: "litify_pm__Matter__c",
                requestedAt: new Date().toISOString(),
              };
              const response = await getUploadSignedUrl(token, fileData);

              await uploadFileToSignedUrl(file, response.signedUrl);
              const uploadResult = await postFileUploadComplete(token, response.fileID);
              await refreshDocuments();

              if (uploadResult.success) {
                setStatusMessage({
                  type: "success",
                  text: t("clientDetails.uploadSuccess"),
                });
              }
            } catch {
              setStatusMessage({
                type: "error",
                text: t("clientDetails.uploadError"),
              });
            } finally {
              setUploading(false);
            }
          }}
          disabled={uploading}
          style={{ display: "none" }}
        />
      </div>
      )}

      {documentsError && <div className="status-message is-error">{documentsError}</div>}
      {statusMessage && (
        <div className={`status-message ${statusMessage.type === "error" ? "is-error" : "is-success"}`}>
          {statusMessage.text}
        </div>
      )}
      </section>
    </div>
  );
};

export default ClientDetailsMatter;
